import { Injectable, signal, effect, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';


export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * Angular Material Theme Service
 *
 * Manages application theming using Angular Material's built-in theme system.
 * This service handles theme switching between light, dark, and system modes
 * by applying the appropriate CSS classes that trigger Material's theme mixins.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private document = inject<Document>(DOCUMENT);

  private mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  // Reactive signals for theme state management
  public currentTheme = signal<ThemeMode>('system');
  public isDark = signal<boolean>(false);

  constructor() {
    // Load initial theme from localStorage
    this.loadTheme();

    // Listen for system theme changes
    this.mediaQuery.addEventListener('change', () => {
      if (this.currentTheme() === 'system') {
        this.applyMaterialTheme();
      }
    });

    // React to theme changes and apply Material Design themes
    effect(() => {
      this.applyMaterialTheme();
    });
  }

  /**
   * Set the application theme
   * Stores the preference in localStorage and applies the theme
   */
  setTheme(theme: ThemeMode): void {
    this.currentTheme.set(theme);
    localStorage.setItem('theme', theme);
    this.applyMaterialTheme();
  }

  /**
   * Load theme preference from localStorage
   * Falls back to 'system' if no valid theme is stored
   */
  private loadTheme(): void {
    const savedTheme = localStorage.getItem('theme') as ThemeMode;
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      this.currentTheme.set(savedTheme);
    }
  }

  /**
   * Apply Material Design theme using CSS classes
   *
   * This method applies the appropriate theme class (.dark-theme or removes it)
   * which triggers Angular Material's theme mixins defined in styles.scss.
   * The Material theme system automatically handles all component styling.
   */
  private applyMaterialTheme(): void {
    const theme = this.currentTheme();
    let isDarkMode = false;

    // Determine the active theme mode
    switch (theme) {
      case 'dark':
        isDarkMode = true;
        break;
      case 'light':
        isDarkMode = false;
        break;
      case 'system':
        isDarkMode = this.mediaQuery.matches;
        break;
    }

    // Update the reactive signal for UI components
    this.isDark.set(isDarkMode);

    // Apply the Material theme class to body element
    // This triggers the .dark-theme CSS rules defined in styles.scss
    const bodyElement = this.document.body;
    if (isDarkMode) {
      bodyElement.classList.add('dark-theme');
    } else {
      bodyElement.classList.remove('dark-theme');
    }

    // Also apply to html element for maximum compatibility
    const htmlElement = this.document.documentElement;
    if (isDarkMode) {
      htmlElement.classList.add('dark-theme');
    } else {
      htmlElement.classList.remove('dark-theme');
    }

    // Set color-scheme for native browser elements (scrollbars, form controls)
    htmlElement.style.colorScheme = isDarkMode ? 'dark' : 'light';
  }

  /**
   * Get available theme options for UI components
   * Returns theme modes with translation keys and Material icons
   */
  getThemeOptions(): {value: ThemeMode, label: string, icon: string}[] {
    return [
      { value: 'light', label: 'THEME.LIGHT', icon: 'light_mode' },
      { value: 'dark', label: 'THEME.DARK', icon: 'dark_mode' },
      { value: 'system', label: 'THEME.SYSTEM', icon: 'settings_brightness' }
    ];
  }
}
